import { Scenario } from "@reco/shared/src/constants";
import type { RecommendationsArgs } from "@reco/shared/src/types/recommendation";
import RecommendationsService from "../index";
import { isStandardRecommendationsResult } from "./helpers";

/**
 * Gets category information for a product by calling the products endpoint
 * @param service RecommendationsService instance
 * @param recoArgs Original recommendation arguments
 * @param categoryLevel Category level to extract (1, 2, 3, etc.)
 * @returns Object containing categoryId and categoryName
 * @throws Error if category path is undefined or scenario returns unexpected result
 */
export async function getCategoryFromProduct(
  service: RecommendationsService,
  recoArgs: RecommendationsArgs,
  categoryLevel = 1,
): Promise<{ categoryId: string; categoryName: string }> {
  const result = await service.getRecommendations({
    ...recoArgs,
    type: Scenario.PRODUCTS,
  });

  if (!isStandardRecommendationsResult(result)) {
    throw new Error(`Unexpected recommendation result type for scenario ${result.scenario}`);
  }

  const { categoryPath, categoryPathName } = result;

  if (!categoryPath) {
    throw new Error("Category path is undefined");
  }

  const categories = categoryPath.split("/").filter(Boolean);
  const categoryId = categories[categoryLevel - 1] || categories[categories.length - 1];

  return {
    categoryId,
    categoryName: getCategoryLevel(categoryLevel, categoryPathName),
  };
}

/**
 * Gets brand information for a product, first checking unified_datalayer_product,
 * then falling back to products endpoint
 * @param service RecommendationsService instance
 * @param recoArgs Original recommendation arguments
 * @returns Object containing brandId and brandName
 */
export async function getBrandFromProduct(
  service: RecommendationsService,
  recoArgs: RecommendationsArgs,
): Promise<{ brandId: string; brandName: string }> {
  let brand = window.unified_datalayer_product?.brand;

  // Fallback to API call if not available in unified_datalayer_product
  if (!brand) {
    const result = await service.getRecommendations({
      ...recoArgs,
      type: Scenario.PRODUCTS,
    });
    if (result.scenario !== Scenario.SHOPTHELOOK) {
      brand = result.brandName;
    }
  }

  if (!brand) {
    throw new Error("Unable to determine brand for product");
  }

  const cleanedBrand = brand.toLowerCase().replace(/[^a-z0-9]+/gi, "");

  return {
    brandId: cleanedBrand,
    brandName: brand,
  };
}

/**
 * Utility function to clean brand names for use as contextId
 * @param brandName Raw brand name
 * @returns Cleaned brand name
 */
export function cleanBrandName(brandName: string): string {
  return brandName.toLowerCase().replace(/[^a-z0-9]+/gi, "");
}

/**
 * Utility function to extract category level from category path
 * @param level Category level to extract (1, 2, 3, etc.)
 * @param categoryPath Category path string
 * @returns Category ID at the specified level
 */
export function getCategoryLevel(level: number, categoryPath?: string): string {
  if (!categoryPath) {
    console.error("[getCategoryLevel]: Category path is undefined");
    return "";
  }
  const categories = categoryPath.split("/").filter(Boolean);
  return categories[level - 1] || categories[categories.length - 1];
}
